// Quota / usage / route-decision contracts (doc 40). The TYPES live here so the
// admin Quotas screen (Phase 3) and the gateway enforcement engine (Phase 4)
// share one definition. Enforcement is implemented in Phase 4.

import type { UsageCacheDetails } from './agent-ir.js';
import type { BillingMode, ModelTier, ProviderProtocol, RouterKind, VelugaRouteMode, WebSearchNativeProvider } from './service.js';
import type { CreditEnforcementMode } from './credit-policy.js';

/** Ordered shortest-first: several call sites rank cycles by how soon a user
 *  feels the cap. `lifetime` is last because it never resets. */
export const QUOTA_CYCLES = ['daily', 'weekly', 'monthly', 'lifetime'] as const;
export type QuotaCycle = (typeof QUOTA_CYCLES)[number];

/** Narrow an untrusted value (HTTP body, jsonb row) to a supported cycle.
 *  `quota_policies.policy` is jsonb, so the database imposes no shape. */
export function isQuotaCycle(value: unknown): value is QuotaCycle {
  return typeof value === 'string' && (QUOTA_CYCLES as readonly string[]).includes(value);
}

export interface QuotaScopeTenant {
  kind: 'tenant';
}
export interface QuotaScopeGroup {
  kind: 'group';
  groupId: string;
}
export interface QuotaScopeUser {
  kind: 'user';
  userId: string;
}
export type QuotaScope = QuotaScopeTenant | QuotaScopeGroup | QuotaScopeUser;

export interface QuotaLimits {
  requests?: number;
  /** Web-search request cap for Gemini grounding search. Does not affect LLM request quota. */
  webSearchRequests?: number;
  /** Image-generation request cap. Separate from vision image input. */
  imageGenerationRequests?: number;
  inputTokens?: number;
  outputTokens?: number;
  totalTokens?: number;
  maxSingleRequestTokens?: number;
  /** Lifetime priced-credit cap (millicredits). Backs prepaid credit
   *  allocations; checked against accumulated `credit.creditMillis`. */
  credit?: number;
}

export interface QuotaSelector {
  /**
   * Exact Veluga catalog model id. MVP supports ONE model and LLM usage only —
   * image/video generation (metered on `media_model_id`) is never counted by a
   * model-scoped limit. This is a metering selector, NOT authorization: a
   * request for another model simply skips this policy.
   */
  catalogModelId?: string;
}

/** Where a cap originated. Evidence and user guidance only: never include this
 * in counter or hydration keys, because changing provenance must not split usage. */
export type QuotaPolicyProvenance = 'funding' | 'profile' | 'member_default' | 'operator';

/** Shared UI/telemetry threshold: warn once 80% of a finite quota is consumed. */
export const QUOTA_WARNING_THRESHOLD = 0.8;

export function isQuotaWarningThresholdReached(consumed: number, limit: number): boolean {
  return limit > 0 && consumed >= limit * QUOTA_WARNING_THRESHOLD;
}

export interface QuotaPolicy {
  id: string;
  tenantId: string;
  scope: QuotaScope;
  cycle: QuotaCycle;
  /** Additive for backward compatibility with policies stored before provenance. */
  provenance?: QuotaPolicyProvenance;
  limits: QuotaLimits;
  rate?: RateLimitSpec;
  /** Optional billing-mode discriminator. Used for BYO token-budget pools so
   *  platform-credit usage cannot consume or trip a BYO raw-token grant. */
  appliesToBillingMode?: BillingMode;
  /** Catalog models that skip this policy entirely. Authorization is unchanged. */
  exemptCatalogModelIds?: readonly string[];
  /** Non-blocking metering selector. Mutually exclusive with `modelAllowlist`. */
  selector?: QuotaSelector;
  /**
   * Legacy AUTHORIZATION constraint: a request whose model is absent from this
   * list is denied (`model_not_allowed`). Never use it as a metering selector.
   * Veluga model ids only; cannot widen the tenant/group/user allowlist.
   */
  modelAllowlist?: string[];
  overageBehavior: 'deny' | 'warn';
}

/** Counter/rate dimension for a policy's metering selector. Shared so the
 *  gateway counter key, the rate key, audit payloads and the deterministic
 *  credit-limit policy id all spell the dimension the same way. */
export function quotaSelectorKey(policy: Pick<QuotaPolicy, 'selector'>): string {
  const model = policy.selector?.catalogModelId;
  return model ? `m:${model}` : 'all';
}

// ── narrow credit-limit API (plan PR-3) ──────────────────────────────────────
// Operators set FOUR things — subject, amount, cycle, model — instead of hand
// authoring a QuotaPolicy. These limits live in the same `quota_policies` table
// but carry a deterministic id so exactly one row exists per dimension.

export const CREDIT_LIMIT_ID_PREFIX = 'climit:';

export interface UpsertCreditLimitRequest {
  scope: QuotaScope;
  cycle: QuotaCycle;
  /** Non-negative safe integer millicredits. 0 means "block outright". */
  creditMillis: number;
  /** Omitted = all models. */
  catalogModelId?: string;
  overageBehavior: 'warn' | 'deny';
  /** An edit that changed the cycle or model moves the row to a new id; this
   *  deletes the old one inside the same idempotent command, so two limits are
   *  never simultaneously in force for what the operator meant as one. */
  replaceId?: string;
}

export interface CreditLimitView {
  id: string;
  scope: QuotaScope;
  cycle: QuotaCycle;
  creditMillis: number;
  catalogModelId?: string;
  overageBehavior: 'warn' | 'deny';
  /** Current-window figures. Display only — the gateway enforces. */
  consumedMillis?: number;
  remainingMillis?: number;
  resetAt?: string;
  /** The selector model left the tenant catalog/assignment. The row is kept. */
  orphanedModel?: boolean;
}

export interface EffectiveCreditLimitsRequest {
  subject: { kind: 'user'; userId: string } | { kind: 'group'; groupId: string };
  /** Preview as if a request used this model. Omitted = all-model limits only. */
  catalogModelId?: string;
}

/** Where an effective limit came from. `funded_allocation` is a cap derived
 *  from prepaid funding; the funding total itself is reported separately in
 *  `funded` and must never be mixed into `limits`. */
export type CreditLimitOrigin =
  | 'operator'
  | 'profile'
  | 'member_default'
  | 'funded_allocation';

export interface EffectiveCreditLimitEntry {
  id: string;
  origin: CreditLimitOrigin;
  scope: QuotaScope;
  cycle: QuotaCycle;
  catalogModelId?: string;
  limitMillis: number;
  consumedMillis: number;
  remainingMillis: number;
  overageBehavior: 'warn' | 'deny';
  resetAt?: string;
  orphanedModel?: boolean;
  /** Smallest remaining within its model dimension = what actually binds. */
  binding?: boolean;
}

export interface EffectiveCreditLimitsView {
  enforcementMode?: CreditEnforcementMode;
  unguardedFreeTierModelIds?: string[];
  /** Funding plane — money, not a limit. Never merged into `limits`. */
  funded: { unlimited: boolean; granted?: number; consumed: number; remaining?: number };
  limits: EffectiveCreditLimitEntry[];
}

/** Colon-free, injective, dependency-free encoding of a model id. Anything
 *  outside [A-Za-z0-9-] becomes `_xNN`, including `_` itself, so the mapping
 *  stays reversible and two different models can never collide.
 *
 *  Deliberately NOT a hash: this module is re-exported by the shared-types
 *  barrel that admin-web bundles for the browser, so `node:crypto` is
 *  unavailable, and a weak pure-TS hash could collide — which would silently
 *  merge two models' limits into one row. */
function escapeModelSegment(catalogModelId?: string): string {
  if (!catalogModelId) return 'all';
  let out = 'm_';
  for (const ch of catalogModelId) {
    out += /^[A-Za-z0-9-]$/.test(ch)
      ? ch
      : `_x${ch.codePointAt(0)!.toString(16).padStart(2, '0')}`;
  }
  return out;
}

/** Deterministic policy id: exactly one active row per (scope, cycle, model).
 *  The tenant scope uses `self` because rows are already isolated by
 *  `tenant_id`, so an act-as tenant switch cannot change the id rule. */
export function creditLimitPolicyId(
  scope: QuotaScope,
  cycle: QuotaCycle,
  catalogModelId?: string
): string {
  const scopeId =
    scope.kind === 'tenant' ? 'self' : scope.kind === 'group' ? scope.groupId : scope.userId;
  return `${CREDIT_LIMIT_ID_PREFIX}${scope.kind}:${scopeId}:${cycle}:${escapeModelSegment(catalogModelId)}`;
}

export interface RateLimitSpec {
  requestsPerMinute?: number;
  tokensPerMinute?: number;
  burst?: number;
}

export type UsageKind = 'realtime_voice' | 'speech_to_text' | 'text_to_speech' | 'llm' | 'route_judge' | 'web_search' | 'image_generation' | 'video_generation';

export type CreditComponentKind =
  | 'realtime_voice'
  | 'speech_to_text'
  | 'text_to_speech'
  | 'llm_tokens'
  | 'web_search'
  | 'image_input'
  | 'image_output'
  | 'video_output';

export interface CreditComponentSnapshot {
  kind: CreditComponentKind;
  units: number;
  unitName: 'token' | 'request' | 'image' | 'second' | 'millisecond';
  cachedUnits?: number;
  cacheWriteUnits?: number;
  nonCachedUnits?: number;
  costMicroUsd: number;
  creditMillis: number;
  /** Authoritative provider cost in nanoUSD for precision-era events. */
  costNanoUsd?: number;
  /** Authoritative charge in microcredits for precision-era events. */
  creditMicros?: number;
  rateVersion: string;
  rateKey: string;
}

export interface CreditSnapshot {
  /** Provider-cost snapshot in integer microUSD. */
  costMicroUsd: number;
  /** Converted credit snapshot in integer millicredits. */
  creditMillis: number;
  /** Authoritative provider cost in nanoUSD for precision-era events. */
  costNanoUsd?: number;
  /** Authoritative charge in microcredits for precision-era events. */
  creditMicros?: number;
  /** Charge waived at commit, preserved for settlement evidence. */
  waivedCreditMicros?: number;
  /** Self-describing billing exemption for historical usage rows. */
  billingExemption?: 'free_tier';
  rateVersion: string;
  components?: CreditComponentSnapshot[];
}

export type CreditPricingStatus = 'priced' | 'missing_rate' | 'byo_unbilled';

/** Token cache detail of a model call; defined with the agent IR usage. */
export type { UsageCacheDetails } from './agent-ir.js';

// ── Hierarchical prepaid credit allocation (doc: partner-admin tier Phase 2) ──

export type AllocationGranterKind = 'platform' | 'partner' | 'tenant';
export type AllocationGranteeKind = 'partner' | 'tenant' | 'group' | 'user';

/** A prepaid grant funding one grantee from one granter. Credit is the single
 *  settlement currency (price-weighted `creditMillis`); the raw token pools are
 *  reserved for custom BYO usage. `unlimited` (platform-set only) exempts the
 *  node from depletion. */
export interface CreditAllocation {
  id: string;
  granterKind: AllocationGranterKind;
  granterId: string;
  granteeKind: AllocationGranteeKind;
  granteeId: string;
  /** Tenant context for group/user (and the tenant itself for tenant grantees). */
  tenantId?: string;
  creditMillisGranted?: number;
  inputTokensGranted?: number;
  outputTokensGranted?: number;
  unlimited: boolean;
  status: 'active' | 'revoked';
  note?: string;
}

export interface MemberQuotaDefault {
  tenantId: string;
  nodeKind: 'tenant' | 'group';
  nodeId: string;
  perMemberCreditMillis?: number;
  /** Required. Rows written before cycles existed are normalized to 'lifetime'
   *  at the persistence boundary, so callers never see undefined. */
  cycle: QuotaCycle;
  mode: 'ceiling' | 'reservation';
  updatedAt?: string;
}

/**
 * The effective per-member defaults, at most ONE per cycle:
 *   1. group rows matching the caller's groups, grouped by cycle
 *   2. within a cycle the LOWEST perMemberCreditMillis wins (tie → nodeId asc)
 *   3. a group row REPLACES the tenant row for that cycle
 *   4. different cycles coexist (tenant lifetime + group weekly is valid)
 *   5. rows without perMemberCreditMillis mean "no cap" and are dropped
 *
 * One row per node caps 10,000 members without creating 10,000 policy rows.
 */
export function resolveMemberQuotaDefaults(
  tenantId: string,
  defaults: readonly MemberQuotaDefault[],
  groupIds: readonly string[]
): MemberQuotaDefault[] {
  const groups = new Set(groupIds);
  const capped = defaults.filter(
    (row) => row.tenantId === tenantId && row.perMemberCreditMillis !== undefined
  );
  const resolved: MemberQuotaDefault[] = [];
  for (const cycle of QUOTA_CYCLES) {
    const forCycle = capped.filter((row) => row.cycle === cycle);
    const group = forCycle
      .filter((row) => row.nodeKind === 'group' && groups.has(row.nodeId))
      .sort(
        (a, b) =>
          a.perMemberCreditMillis! - b.perMemberCreditMillis! || a.nodeId.localeCompare(b.nodeId)
      )[0];
    const tenant = forCycle.find(
      (row) => row.nodeKind === 'tenant' && row.nodeId === tenantId
    );
    const chosen = group ?? tenant;
    if (chosen) resolved.push(chosen);
  }
  return resolved;
}

/** A grantee's allocation vs its consumption, with its direct children. */
export interface AllocationOverview {
  granteeKind: AllocationGranteeKind;
  granteeId: string;
  unlimited: boolean;
  /** Price-weighted credit pool. `granted`/`remaining` are undefined when
   *  unlimited or unallocated. */
  credit: { granted?: number; consumed: number; remaining?: number };
  children: AllocationChild[];
}

export interface AllocationChild {
  granteeKind: AllocationGranteeKind;
  granteeId: string;
  name?: string;
  unlimited: boolean;
  creditGranted?: number;
  creditConsumed: number;
}

export interface UsageEvent {
  realtimeVoice?: import('./realtime.js').RealtimeVoiceUsageEvidence;
  speech?: import('./realtime.js').SpeechUsageEvidence;
  seq?: number;
  id: string;
  tenantId: string;
  userId: string;
  groupIds: string[];
  sessionId: string;
  /** Client-provided analytics/join label; never trusted for authorization or billing. */
  conversationId?: string;
  requestId: string;
  routeDecisionId?: string;
  catalogModelId: string | null;
  effectiveCatalogModelId?: string | null;
  backendId?: string;
  routeMode?: VelugaRouteMode;
  inputTokens: number;
  outputTokens: number;
  totalTokens: number;
  /** @deprecated Use credit.costMicroUsd. */
  estimatedCostUsd?: number;
  /** @deprecated Use credit.costMicroUsd. */
  actualCostUsd?: number;
  credit?: CreditSnapshot;
  /** Explicit pricing classification for precision-era usage evidence. */
  pricingStatus?: CreditPricingStatus;
  /** Provider cache evidence; absence means the legacy/unknown shape. */
  cache?: UsageCacheDetails;
  /** Default 'llm' for legacy events without this field. */
  usageKind?: UsageKind;
  webSearchProvider?: WebSearchNativeProvider;
  webSearchRequests?: number;
  imageInputCount?: number;
  imageInputBytes?: number;
  /** Content-free native attachment counts/bytes. Raw attachment data is never
   * persisted in usage evidence. */
  attachmentInput?: import('./native-attachment.js').NormalizedAttachmentUsage;
  mediaRouteId?: string;
  mediaModelId?: string;
  imageGenerationCount?: number;
  videoGenerationSeconds?: number;
  /** Stable, content-free billing variant (for example `size=1024x1024`). */
  generationVariantKey?: string;
  prevHash?: string;
  eventHash?: string;
  createdAt: string;
}

export type RouteDecisionReason =
  | 'fixed'
  | 'policy_strong_only'
  | 'local_only'
  | 'capability_required'
  | 'quota_guardrail'
  | 'adaptive_score'
  | 'pure_greeting'
  | 'forced_strong_final_answer'
  | 'forced_strong_compliance'
  | 'denied_task_kind'
  | 'denied_risk_level'
  | 'denied_data_classification'
  | 'closed_network_blocked'
  | 'adaptive_disabled'
  | 'fallback';

export interface RouteDecisionEvent {
  id: string;
  tenantId: string;
  userId: string;
  groupIds: string[];
  sessionId: string;
  conversationId?: string;
  requestId: string;
  catalogModelId: string;
  /** Concrete model selected for execution; absent on legacy fixed-route events. */
  effectiveCatalogModelId?: string;
  routeMode: VelugaRouteMode;
  router: RouterKind;
  shadow: boolean;
  selectedBackendId: string;
  selectedTier: ModelTier;
  executionTier?: ModelTier;
  /** Execution protocol of the selected route (e.g. `openai-responses-compatible`
   *  vs `openai-compatible`). Two catalog models can share a backend but differ
   *  in protocol, so this disambiguates which executor ran. Optional for
   *  backward-compat with pre-existing event records. */
  selectedProtocol?: ProviderProtocol;
  candidateBackendIds: string[];
  decisionReason: RouteDecisionReason;
  adaptiveScore?: number;
  adaptiveThreshold?: number;
  fallbackFromBackendId?: string;
  policyHash: string;
  createdAt: string;
}

export type QuotaLimitName =
  | 'requests'
  | 'webSearchRequests'
  | 'imageGenerationRequests'
  | 'inputTokens'
  | 'outputTokens'
  | 'totalTokens'
  | 'maxSingleRequestTokens'
  | 'credit';

export interface QuotaExceededError {
  code: 'quota_exceeded';
  scope: 'tenant' | 'group' | 'user';
  limitName: QuotaLimitName;
  cycle: QuotaCycle;
  resetAt: string;
  message: string;
  blockedBy?: QuotaBlockedBy;
  /** Sanitized presentation hint; policy ids and assignments stay in blockedBy/server state. */
  freeTierAvailable?: boolean;
}

export interface QuotaBlockedBy {
  provenance: QuotaPolicyProvenance;
  cycle: QuotaCycle;
  scopeKind: 'tenant' | 'group' | 'user';
  /** Omitted for lifetime caps, which do not refill. */
  resetAt?: string;
  /** Audit evidence only. Clients must not render this identifier. */
  policyId: string;
}

/** Localize the server-authoritative UTC reset instant without combining
 * dateStyle/timeStyle with timeZoneName (an invalid Intl option combination). */
export function formatQuotaResetAt(iso: string | undefined, locale: string): string | undefined {
  if (!iso) return undefined;
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return undefined;
  return new Intl.DateTimeFormat(locale, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    timeZoneName: 'short'
  }).format(at);
}

export interface PolicyContextUsageSummary {
  tenantRemaining?: number;
  groupRemaining?: number;
  userRemaining?: number;
  resetAt?: string;
  enforcement: 'gateway' | 'disabled';
}
